import { FC, useState, useEffect, useMemo } from 'react';
import EditorImg from 'lowcode/components/EditorImg';
import ProductHeader from '../ProductHeader';
import styled from './index.module.scss';
import Icon from './Icon';
import LockIcon from './LockIcon';
import FlashSaleCountdown from './FlashSaleCountdown';
import ProductItem from './item';
import JPProductItem from './jpItem';
import { useLang } from '@/i18n';
import getLocal from '@/app/utils/getLocal';
import { useMembersDispatch, useMembersStore } from '@/components/MembersDay/MembersProvider';
import wss from '@/utils/mqtt-client';
import { getWSSUrl } from '@/utils/countryRegion';
import topics from '@/utils/mqtt-client/crowd-funding-topics';
import { getListSecKillProducts } from '@/service/membersday';
import { message } from '@/components/UI/Message';
import { useGlobal } from '@/utils/useGlobal';
import { formatPrice } from '@/components/MembersDay/utils';
import { EuEcoCredits } from '@/components/MembersDay/FY2024/CommonElement/EuEcoCredits';
import { EU_SITES, NA_SITES } from '@/constants/membersday';
import { signupCookie } from '@/utils/cookies/signupCookie';

interface IProps {
    type?: string;
}

const FlashSale: FC<IProps> = ({}) => {
    const { getSystemTime } = useGlobal();
    const { t } = useLang('membersday.activity');
    const { locale } = getLocal();
    const { isLogin, secKillisBegin, secKillProduct } = useMembersStore();
    const dispatch = useMembersDispatch();

    // eu 未登录时样式与正常产品模块结构有差异
    const isEuUnlogin = EU_SITES.includes(locale) && !isLogin;

    // 秒杀时间
    const [timeInfo, setTimeInfo] = useState({
        secKillStartTime: 0,
        secKillEndTime: 0,
    });

    const handleData = (data: any) => {
        if (!data) return;
        const { productList, secKillStartTime, secKillEndTime } = data;
        const now = getSystemTime();

        let begin = false;

        // jp忽略
        if (now > Number(secKillStartTime)) begin = true;
        if (now > Number(secKillEndTime)) {
            dispatch({
                type: 'changeSecKillProduct',
                payload: [],
            });
            return;
        }

        productList.map((item: any) => {
            const newItem = { ...item };
            const hidePrice = locale !== 'jp' ? !begin : false;

            item.discountPercent = begin ? item.discountPercent : '??';
            item.memberPrice = formatPrice(item.memberPrice, locale, hidePrice);
            item.price = formatPrice(item.price, locale, hidePrice);
            item.disparityPrice = begin ? formatPrice(String(parseFloat(newItem.compareAtPrice) - parseFloat(newItem.memberPrice)), locale) : '??';
            item.compareAtPrice = formatPrice(item.compareAtPrice, locale);

            // jp展示原价
            if (locale === 'jp') {
                item.discountPercent = newItem.discountPercent;
            }

            if (locale === 'za') {
                item.disparityPrice += ' OFF';
            }
            if (NA_SITES.includes(locale)) {
                item.disparityPrice = `SAVE ${item.disparityPrice}`;
            }
        });

        dispatch({
            type: 'changeSecKillProduct',
            payload: productList,
        });
        dispatch({
            type: 'changeSecKillisBegin',
            payload: begin,
        });
        setTimeInfo({
            secKillStartTime: Number(secKillStartTime) || 0,
            secKillEndTime: Number(secKillEndTime) || 0,

            // secKillStartTime: new Date().getTime() + 1000000,
            // secKillEndTime: new Date().getTime() + 2000000,
        });
    };

    const { receiver, mqttUnsubscribe, mqttSubscribe } = wss({
        url: String(getWSSUrl(locale)),
        topics,
        locale,
        subscribes: ['ecocreditsYear'],
    });

    const productDesc = useMemo(() => {
        if (!secKillisBegin && locale === 'jp') {
            return t('product_desc1_before');
        }
        return t('product_desc1');
    }, [locale, secKillisBegin]);

    useEffect(() => {
        if (!receiver || !receiver.data) return;
        handleData(receiver.data);
    }, [receiver]);

    const initData = () => {
        getListSecKillProducts(locale).then((res) => {
            if (res.code !== '0') {
                message.error(res.message);
                return;
            }

            handleData(res.data);
        });
    };

    const handleEnd = () => {
        if (!secKillisBegin) {
            dispatch({
                type: 'changeSecKillisBegin',
                payload: true,
            });
        } else {
            dispatch({
                type: 'changeSecKillProduct',
                payload: [],
            });
        }
    };

    const handleLogin = () => {
        const pathname2 = location.pathname;
        signupCookie?.({
            sector_name: t('product_title1'),
            entry_position: 3,
        });
        location.href = `/${locale}/login?redirect=${pathname2}`;
    };

    useEffect(() => {
        initData();
        mqttSubscribe('ecocreditsYear');
        return () => mqttUnsubscribe('ecocreditsYear');
    }, []);

    if (!secKillProduct || secKillProduct.length === 0) return <></>;

    return (
        <div className={`${styled.FlashSale}`} id="activity-FlashSale">
            <ProductHeader icon={<Icon />} title={t('product_title1')} desc={productDesc} />
            <FlashSaleCountdown timeInfo={timeInfo} handleEnd={handleEnd} />
            {!isEuUnlogin && (
                <div className="product_list">
                    {secKillProduct.map((item) =>
                        locale === 'jp' ? (
                            <JPProductItem key={item.id} item={item} list_name={t('product_title1')} />
                        ) : (
                            <ProductItem key={item.id} item={item} list_name={t('product_title1')} />
                        ),
                    )}
                </div>
            )}
            {isEuUnlogin && (
                <div className="product_unlogin">
                    <div className="product_unlogin_list">
                        {secKillProduct
                            .filter((_item, index) => index < 3)
                            .map((item) => (
                                <div key={item.id} className="lock_item">
                                    {item.discountPercent && (
                                        <div className="off">
                                            {item.discountPercent}%&nbsp;
                                            <span>{t('off')}</span>
                                        </div>
                                    )}
                                    <div className="lock_top">
                                        <div className="lock_img">
                                            <EditorImg pc={item.imageUrl} />
                                        </div>
                                        <EuEcoCredits />
                                        <div className="lock_name">{item.title}</div>
                                        {item.subtitle1 && <p className="lock_subtitle">{item.subtitle1}</p>}
                                        <div>
                                            <div className="lock_price">
                                                {item.memberPrice || item.price}
                                                <span>{item.compareAtPrice}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="lock_mobile" onClick={handleLogin}>
                                        <LockIcon />
                                        {t('flashsale.lock')}
                                    </div>
                                </div>
                            ))}
                        <div className="lock" onClick={handleLogin}>
                            <LockIcon />
                            {t('flashsale.lock')}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FlashSale;
